import { createSlice } from "@reduxjs/toolkit";
import axios from "utils/axios";
import { dispatch } from "../index";
import { storage } from "../../utils/helpers/storage";
import {
  getDashboardsByAccess,
  sortDashboardsByName,
} from "../../utils/helpers";
import {
  getDashboardIdFromUrl,
  getFilteredResultsByFilterName,
} from "./analytics.helpers";
import { FilterName } from "../../utils/constants/analytics";
import { handleApiError, is401Error } from "../../utils/errorHandling";
import { isCacheValid } from "../../utils/helpers/filterCache";

// Simple memory cache for filter data (cleared on page refresh)
let filterCache = {};
// Track in-flight requests to prevent race conditions
let pendingRequests = {};

// Core cached API call wrapper for services/warehouse/filter endpoint
async function getCachedSiteFilter(warehouse) {
  const cached = filterCache[warehouse];
  
  if (cached && isCacheValid(cached)) {
    return cached.data; // Return cached response.data.data
  }
  
  // Check if request is already in progress
  if (pendingRequests[warehouse]) {
    return pendingRequests[warehouse]; // Return the same promise
  }
  
  // Start new request and cache the promise
  pendingRequests[warehouse] = (async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVICES_API}/services/warehouse/filter`,
        {
          headers: {
            "locus-warehouse": warehouse,
          },
        }
      );
      
      // Cache and return the data
      const payload = response?.data?.data;
      filterCache[warehouse] = { data: payload, fetchedAt: Date.now() };
      return payload;
    } finally {
      // Always clean up the pending request
      delete pendingRequests[warehouse];
    }
  })();
  
  return pendingRequests[warehouse];
}

// ----------------------------------------------------------------------

export const initialState = {
  error: null,
  allDashboards: [],
  availableDashboards: [],
  analyticsCategory: "",
  analyticsSection: "",
  dashboards: [],
  availableWarehouseInductAreas: [],
  availableWarehouseAngles: [],
  availableWarehouseCustowners: [],
  availableWarehouseFloors: [],
  availableWarehouseMeasureUnits: [],
  availableWarehouseOpenWorkStatus: [],
  availableWarehouseOrderTypes: [],
  availableWarehouseSegments: [],
  availableWarehouseTaskTypes: [],
  availableWarehouseTaskZones: [],
  availableWarehouseWorkStatus: [],
  availableWorkAreaGrouping: [],
  selectedDashboard: storage.getSelectedDashboard()
    ? storage.getSelectedDashboard()
    : getDashboardIdFromUrl(),
  selectedPage: "ReportSection",
  selectedSection: null,
  showReportSplash: true,
  results: {
    top: {},
    integritytest: { rows: [], count: 0 },
  },
  warehouseFilterOptions: null,
  defaultReportDate: null,
};

const slice = createSlice({
  name: "analytics",
  initialState,
  reducers: {
    // HAS ERROR
    hasError(state, action) {
      state.error = action.payload;
    },
    getDashboardsSuccess(state, action) {
      state.dashboards = action.payload;
    },
    setAllDashboards(state, action) {
      state.allDashboards = action.payload;
    },
    setAvailableDashboards(state, action) {
      state.availableDashboards = action.payload;
    },
    setAnalyticsCategory(state, action) {
      state.analyticsCategory = action.payload;
    },
    setAnalyticsSection(state, action) {
      state.analyticsSection = action.payload;
    },
    setSelectedDashboard(state, action) {
      storage.setSelectedDashboard(action?.payload?.id || "");
      state.selectedDashboard = action.payload;
    },
    setAvailableWarehouseCustowners(state, action) {
      state.availableWarehouseCustowners = action.payload;
    },
    setAvailableWarehouseFloors(state, action) {
      state.availableWarehouseFloors = action.payload;
    },
    setAvailableWarehouseMeasureUnits(state, action) {
      state.availableWarehouseMeasureUnits = action.payload;
    },
    setAvailableWarehouseAngles(state, action) {
      state.availableWarehouseAngles = action.payload;
    },
    setAvailableWarehouseTaskTypes(state, action) {
      state.availableWarehouseTaskTypes = action.payload;
    },
    setAvailableWarehouseWorkStatus(state, action) {
      state.availableWarehouseWorkStatus = action.payload;
    },
    setAvailableWorkAreaGrouping(state, action) {
      state.availableWorkAreaGrouping = action.payload;
    },
    setAvailableWarehouseOpenWorkStatus(state, action) {
      state.availableWarehouseOpenWorkStatus = action.payload;
    },
    setAvailableWarehouseOrderTypes(state, action) {
      state.availableWarehouseOrderTypes = action.payload;
    },
    setAvailableWarehouseSegments(state, action) {
      state.availableWarehouseSegments = action.payload;
    },
    setAvailableWarehouseTaskZones(state, action) {
      state.availableWarehouseTaskZones = action.payload;
    },
    setAvailableWarehouseInductAreas(state, action) {
      state.availableWarehouseInductAreas = action.payload;
    },
    setShowReportSplash(state, action) {
      state.showReportSplash = action.payload;
    },
    setSelectedPage(state, action) {
      state.selectedPage = action.payload;
    },
    setSelectedSection(state, action) {
      state.selectedSection = action.payload;
    },
    resetAnalyticsFilters(state) {
      // First reset all warehouse filters
      slice.caseReducers.resetWarehouseFilters(state);

      // Then reset additional analytics-specific fields
      state.availableWarehouseMeasureUnits =
        initialState.availableWarehouseMeasureUnits;
      state.availableWarehouseAngles = initialState.availableWarehouseAngles;
      state.availableWarehouseOpenWorkStatus =
        initialState.availableWarehouseOpenWorkStatus;
      state.availableWarehouseOrderTypes =
        initialState.availableWarehouseOrderTypes;
      state.availableWarehouseWorkStatus =
        initialState.availableWarehouseWorkStatus;
      state.warehouseFilterOptions = initialState.warehouseFilterOptions;
    },
    resetWarehouseFilters(state) {
      state.availableWarehouseCustowners =
        initialState.availableWarehouseCustowners;
      state.availableWarehouseFloors = initialState.availableWarehouseFloors;
      state.availableWarehouseSegments = initialState.availableWarehouseSegments;
      state.availableWarehouseTaskTypes =
        initialState.availableWarehouseTaskTypes;
      state.availableWarehouseTaskZones =
        initialState.availableWarehouseTaskZones;
      state.availableWorkAreaGrouping = initialState.availableWorkAreaGrouping;
      state.availableWarehouseInductAreas =
        initialState.availableWarehouseInductAreas;
    },
    setIntegrityTestResults(state, action) {
      state.results.integritytest = action.payload;
    },
    setTopAnalyticsResults(state, action) {
      state.results.top = action.payload;
    },
    setWarehouseFilterOptions(state, action) {
      state.warehouseFilterOptions = action.payload;
    },
    setDefaultReportDate(state, action) {
      state.defaultReportDate = action.payload;
    },
  },
  // extraReducers: (builder) => {
  //   builder
  //     .addCase(onSetSelectedSite, (state, action) => {
  //       // Clear analytics filters when site changes
  //       slice.caseReducers.resetAnalyticsFilters(state);
  //     })
  //     .addDefaultCase((state, action) => {});
  // },
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

// Utility to reassign filters to pageFilters if present
export function assignPageFilters(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => {
    if (row?.pageFilters) {
      return { ...row, filters: row?.pageFilters };
    }
    return row;
  });
}

export function getAllDashboards() {
  return async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API}/analytics/analytics/dashboard`,
        {},
        {},
      );

      let _allDashboards = [];

      if (response?.data?.count > 0) {
        const rows = assignPageFilters(response?.data?.rows);

        _allDashboards = rows
          .sort(sortDashboardsByName())
          .filter((row) => row?.type === "dashboard");
      }
      dispatch(slice.actions.setAllDashboards(_allDashboards));
      return _allDashboards;
    } catch (error) {
      handleApiError(error, dispatch, slice.actions.hasError);
    }
  };
}

export function getAllDashboardsRaw() {
  return async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API}/analytics/analytics/dashboard`,
      );

      if (response?.data?.rows) {
        return assignPageFilters(response?.data?.rows);
      }
      return [];
    } catch (error) {
      handleApiError(error, dispatch, slice.actions.hasError);
      return [];
    }
  };
}
// Helper function to populate Redux state from filter data
function populateFiltersFromData(payload, filters) {
  dispatch(
    slice.actions.setAvailableWarehouseCustowners(
      getFilteredResultsByFilterName(
        FilterName.CUST_OWNER,
        payload?.custOwners?.rows,
        filters,
      ),
    ),
  );

  dispatch(
    slice.actions.setAvailableWarehouseFloors(
      getFilteredResultsByFilterName(
        FilterName.FLOOR,
        payload?.floors?.rows,
        filters,
      ),
    ),
  );

  dispatch(
    slice.actions.setAvailableWarehouseMeasureUnits(
      getFilteredResultsByFilterName(
        FilterName.UNIT_OF_MEASURE,
        payload?.unitOfMeasure?.rows,
        filters,
      ),
    ),
  );

  dispatch(
    slice.actions.setAvailableWarehouseAngles(
      getFilteredResultsByFilterName(
        FilterName.ROTATION,
        payload?.angles?.rows,
        filters,
      ),
    ),
  );

  dispatch(
    slice.actions.setAvailableWarehouseTaskTypes(
      getFilteredResultsByFilterName(
        FilterName.TASK_TYPE,
        payload?.taskTypes?.rows,
        filters,
      ),
    ),
  );

  dispatch(
    slice.actions.setAvailableWarehouseOpenWorkStatus(
      getFilteredResultsByFilterName(
        FilterName.OPEN_WORK_STATUS,
        payload?.openWorkStatus?.rows,
        filters,
      ),
    ),
  );

  dispatch(
    slice.actions.setAvailableWarehouseOrderTypes(
      getFilteredResultsByFilterName(
        FilterName.ORDER_TYPE,
        payload?.orderTypes?.rows,
        filters,
      ),
    ),
  );

  dispatch(
    slice.actions.setAvailableWarehouseWorkStatus(
      getFilteredResultsByFilterName(
        FilterName.WORK_STATUS,
        payload?.workStatus?.rows,
        filters,
      ),
    ),
  );

  dispatch(
    slice.actions.setAvailableWorkAreaGrouping(
      getFilteredResultsByFilterName(
        FilterName.WORK_AREA_GROUPING,
        payload?.workAreaGrouping?.rows,
        filters,
      ),
    ),
  );

  dispatch(
    slice.actions.setAvailableWarehouseTaskZones(
      getFilteredResultsByFilterName(
        FilterName.TASK_ZONE,
        payload?.taskZones?.rows,
        filters,
      ),
    ),
  );

  dispatch(
    slice.actions.setAvailableWarehouseInductAreas(
      getFilteredResultsByFilterName(
        FilterName.INDUCT_AREA,
        payload?.inductAreas?.rows,
        filters,
      ),
    ),
  );

  dispatch(
    slice.actions.setAvailableWarehouseSegments(
      getFilteredResultsByFilterName(
        FilterName.SEGMENT,
        payload?.segments?.rows,
        filters,
      ),
    ),
  );

  dispatch(slice.actions.setWarehouseFilterOptions(payload));
}

export function getAnalyticsFilters(warehouse, filters) {
  return async () => {
    try {
      // Use the cached wrapper function
      const payload = await getCachedSiteFilter(warehouse);

      // Check for filters in payload any object keys
      if (
        !payload ||
        Object.keys(payload)?.length === 0
      ) {
        if (!storage.getKioskId()) window.location.reload(); // Reload the page if there are no keys in the response data
      }

      // Populate Redux state from API data
      populateFiltersFromData(payload, filters);
    } catch (error) {
      if (!is401Error(error)) {
        dispatch(slice.actions.resetAnalyticsFilters());
      }
      handleApiError(error, dispatch, slice.actions.hasError);
    }
  };
}
export function getWarehouseFilters(warehouse) {
  return async () => {
    try {
      // Use the cached wrapper function
      const payload = await getCachedSiteFilter(warehouse);

      dispatch(
        slice.actions.setAvailableWarehouseCustowners(
          payload?.custOwners?.rows,
        ),
      );
      dispatch(
        slice.actions.setAvailableWarehouseFloors(payload?.floors?.rows),
      );
      dispatch(
        slice.actions.setAvailableWarehouseTaskTypes(payload?.taskTypes?.rows),
      );
      dispatch(
        slice.actions.setAvailableWarehouseTaskZones(payload?.taskZones?.rows),
      );

      dispatch(
        slice.actions.setAvailableWorkAreaGrouping(
          payload?.workAreaGrouping?.rows,
        ),
      );

      dispatch(
        slice.actions.setAvailableWarehouseInductAreas(
          payload?.inductAreas?.rows,
        ),
      );

      dispatch(
        slice.actions.setAvailableWarehouseOpenWorkStatus(
          payload?.openWorkStatus?.rows,
        ),
      );

      dispatch(
        slice.actions.setAvailableWarehouseSegments(payload?.segments?.rows),
      );
    } catch (error) {
      if (!is401Error(error)) {
        dispatch(slice.actions.resetWarehouseFilters());
      }
      handleApiError(error, dispatch, slice.actions.hasError);
    }
  };
}

// Function to check if filters exist for every row and section
const allHaveFilters = (rows) => {
  if (!rows || rows.length === 0) return false;

  // Ensure every row has filters and, if sections exist, every section has filters
  return rows.every((row) => {
    // Check if filters are present directly at the row level
    const rowHasFilters = row?.filters ? !!row?.filters?.length : true;

    // If sections exist and have content, check if every section has filters
    const sectionsHaveFilters = (row?.sections?.length && row.sections.length > 0)
      ? row.sections.every((section) => section?.filters?.length > 0)
      : true; // If no sections or empty sections array, skip this check

    // Both the row and all sections (if any) must have filters
    return rowHasFilters && sectionsHaveFilters;
  });
};

export function getDashboards({
  analyticsCategory,
  analyticsSection,
  selectedSite,
  ignoreFilters = false,
  activeUser = null,
}) {
  return async () => {
    try {
      let params = {};
      if (analyticsCategory) {
        params.category = analyticsCategory;
      }
      if (analyticsSection) {
        analyticsSection =
          analyticsSection === "reports"
            ? "report"
            : analyticsSection === "dashboards"
              ? "dashboard"
              : null;
        params.type = analyticsSection;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API}/analytics/analytics/dashboard`,
        { params },
        {},
      );

      const rows = assignPageFilters(response?.data?.rows);

      if (!ignoreFilters) {
        // Check for filters in all rows and sections
        const filtersExistEverywhere = allHaveFilters(rows);

        // If any row or section lacks filters, refresh the page
        if (!filtersExistEverywhere) {
          window.location.reload();
        }
      }

      let _availableDashboards = [];
      if (selectedSite?.id && response?.data?.count > 0) {
        _availableDashboards = getDashboardsByAccess(
          { ...response?.data, rows },
          analyticsSection,
          selectedSite,
          activeUser,
        );
        dispatch(slice.actions.setAnalyticsSection(analyticsSection));
      }
      dispatch(slice.actions.setAnalyticsCategory(analyticsCategory));
      dispatch(slice.actions.setAvailableDashboards(_availableDashboards));
      dispatch(slice.actions.getDashboardsSuccess({ ...response?.data, rows }));

      let currentSelectedDashboardId = storage.getSelectedDashboard()
        ? storage.getSelectedDashboard()
        : getDashboardIdFromUrl();

      if (
        currentSelectedDashboardId &&
        _availableDashboards &&
        _availableDashboards.some((e) => e?.id === currentSelectedDashboardId)
      ) {
        dispatch(
          slice.actions.setSelectedDashboard(
            _availableDashboards.find(
              (e) => e?.id === currentSelectedDashboardId,
            ),
          ),
        );
      }
      return { ...response, data: { ...response?.data, rows } };
    } catch (error) {
      handleApiError(error, dispatch, slice.actions.hasError);
    }
  };
}

export function getReportUrl(data) {
  return async () => {
    try {
      let headers = {
        "Content-Type": "application/json",
        "locus-powerbi": data.powerBi,
        "locus-user": `{"username":"${data.userName}"}`,
        "locus-dataset-id": data.datasetId,
        "locus-warehouse": data.condensedName,
        "locus-client-id": data.clientId,
      };
      if (data?.kioskName) {
        headers["locus-kiosk-name"] = data.kioskName;
      }
      let response = await axios.get(
        `${process.env.REACT_APP_API}/analytics/analytics/report/${data.reportId}`,
        {
          headers: headers,
        },
      );
      return response;
    } catch (error) {
      handleApiError(error, dispatch, slice.actions.hasError);
    }
  };
}

export function createDashboard(data) {
  return async () => {
    try {
      return data;
      // return axios.post(
      //     `${process.env.REACT_APP_SERVICES_API}/analytics/dashboard`,
      //     data,
      //     {}
      // );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

export function setSelectedDashboard(dashboard) {
  return async () => {
    try {
      dispatch(slice.actions.setSelectedDashboard(dashboard));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function setShowReportSplash(value) {
  return async () => {
    try {
      dispatch(slice.actions.setShowReportSplash(value));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function setSelectedPage(value) {
  return async () => {
    try {
      dispatch(slice.actions.setSelectedPage(value));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function setSelectedSection(value) {
  return async () => {
    try {
      dispatch(slice.actions.setSelectedSection(value));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getAnalyticsResults({
  page = 1,
  pageSize = 20,
  sort = "name",
  sortDirection = "DESC",
  id,
  site,
  client,
  selectedClient,
}) {
  return async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API}/analytics/analytics/results`,
        {
          params: {
            page,
            pageSize,
            sort,
            sortDirection,
            id,
            site,
            client,
          },
          headers: {
            "locus-domain": selectedClient.domain,
            "locus-region": storage.getRegion(),
          },
        },
      );

      if (response?.data?.rows) {
        // Process the response rows if needed
        console.log("Analytics Results:", response.data.rows);
        return response.data.rows;
      } else {
        console.warn("No results found.");
        return [];
      }
    } catch (error) {
      console.error("Error fetching analytics results:", error);
      handleApiError(error, dispatch, slice.actions.hasError);
      return [];
    }
  };
}

export function getIntegrityTestResults({
  page = 1,
  pageSize = 20,
  sort = "id",
  sortDirection = "DESC",
  client,
  site,
  status,
  result,
  submittedBy,
  submittedAt,
  startAt,
  endAt,
  id,
  testOrigin,
}) {
  return async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API}/analytics/analytics/integritytest`,
        {
          params: {
            page,
            pageSize,
            sort,
            sortDirection,
            client,
            site,
            status,
            submittedBy,
            submittedAt,
            startAt,
            endAt,
            result,
            id,
            testOrigin,
          },
        },
      );

      if (response?.data?.rows) {
        // Dispatch the results to update the state
        dispatch(slice.actions.setIntegrityTestResults(response.data));
        return response.data.rows;
      } else {
        console.warn("No integrity test results found.");
        return [];
      }
    } catch (error) {
      console.error("Error fetching integrity test results:", error);
      handleApiError(error, dispatch, slice.actions.hasError);
      return [];
    }
  };
}

export function getTopAnalyticsResults(selectedClient) {
  return async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API}/analytics/analytics/results/top`,
        {
          headers: {
            "locus-domain": selectedClient.domain,
            "locus-region": storage.getRegion(),
          },
        },
      );

      console.log("Top Analytics Results:", response.data);
      if (response?.data) {
        // Dispatch the results to update the state
        dispatch(slice.actions.setTopAnalyticsResults(response.data));
        return response.data;
      } else {
        console.warn("No top analytics results found.");
        return {};
      }
    } catch (error) {
      console.error("Error fetching top analytics results:", error);
      handleApiError(error, dispatch, slice.actions.hasError);
      return {};
    }
  };
}

export function postIntegrityTestResults({ client, site, startAt, endAt }) {
  return async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API}/analytics/analytics/integritytest`,
        {
          client,
          site,
          startAt,
          endAt,
        },
      );

      if (response?.data) {
        // Dispatch the results to update the state
        dispatch(slice.actions.setIntegrityTestResults(response.data));
        return response.data;
      } else {
        console.warn("No integrity test results found.");
        return {};
      }
    } catch (error) {
      console.error("Error posting integrity test results:", error);
      handleApiError(error, dispatch, slice.actions.hasError);
      return {};
    }
  };
}
export function getWarehouseFilterOptions(warehouse) {
  return async () => {
    try {
      // Use the cached wrapper function
      const payload = await getCachedSiteFilter(warehouse);
      dispatch(slice.actions.setWarehouseFilterOptions(payload));
    } catch (error) {
      if (!is401Error(error)) {
        dispatch(slice.actions.setWarehouseFilterOptions(null));
      }
      handleApiError(error, dispatch, slice.actions.hasError);
    }
  };
}

export function setDefaultReportDate(value) {
  return async () => {
    try {
      dispatch(slice.actions.setDefaultReportDate(value));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function resetAnalyticsFilters() {
  return () => {
    dispatch(slice.actions.resetAnalyticsFilters());
  };
}
